import { observer } from 'mobx-react';
import React from 'react';
import { BaseCheckbox, ISelectOption } from '../../base';
import { RULE_FIELD_HELPERS } from '../../../services/worker-configuration/automation/worker-automation.constants';
import {
    AutomationRule,
    AutomationWorkerId,
    MESSAGE_CATEGORIES,
    ReconsiderationDocument,
    RuleActionType,
} from '../../../services/worker-configuration/automation/worker-automation.types';
import {
    ENTER_TASK_FEE_DISCLAIMER,
    getAvailableRuleActionTypes,
    isEnterTaskMessageOwner,
    resolveRuleActionType,
    RULE_ACTION_SELECT_LABELS,
} from '../../../services/worker-configuration/automation/worker-automation.utils';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import {
    ConfigurationActionSummary,
    ConfigurationFieldError,
    ConfigurationFieldLabel,
    ConfigurationRequiredMark,
} from './configuration-action-summary';
import { ConfigurationAssignedUsers } from './configuration-assigned-users';
import { ConfigurationCoderRulesSelect } from './configuration-coder-rules-select';
import { ConfigurationFunctionBuilder } from './configuration-function-builder';
import { ConfigurationFunctionEditor } from './configuration-function-editor';
import { ConfigurationReconsiderationDocuments } from './configuration-reconsideration-documents';
import * as S from './configuration-rule.styles';
import { SegmentButton, SegmentedControl } from '../detail/option-field.styles';

interface IConfigurationRuleActionPanelProps {
    disabled?: boolean;
    /** Must be a rule in `store`'s draft for the selected automation worker: every edit goes through the store by id. */
    rule: AutomationRule;
    store: WorkerStore;
    workerId: AutomationWorkerId;
}

/**
 * A rule's THEN half — the action picker and each action's fields — as the Workers page shows it. One
 * implementation for the Workers page's rule cards and the Denials & Rejections rule editor (FE-116: "It needs
 * to present the same actions as the configuration page"), so the two cannot drift.
 */
export const ConfigurationRuleActionPanel: React.FC<IConfigurationRuleActionPanelProps> = observer(
    ({ disabled, rule, store, workerId }) => {
        const [isCreatingCategory, setIsCreatingCategory] = React.useState(false);
        const [categoryDraft, setCategoryDraft] = React.useState('');
        const resolvedActionType = resolveRuleActionType(rule.action.type);
        const actionValue = rule.action.value;
        const reconsiderationDocuments = (actionValue.documents as ReconsiderationDocument[] | undefined) ?? [];

        const actionSelectOptions = getAvailableRuleActionTypes(workerId).map(value => ({
            label: RULE_ACTION_SELECT_LABELS[value],
            value,
        }));
        const selectedActionOption =
            actionSelectOptions.find(option => option.value === rule.action.type) ?? actionSelectOptions[0];
        const configuredCategory = String(actionValue.messageCategory ?? '').trim();
        const messageCategoryValues = Array.from(
            new Set(
                [...MESSAGE_CATEGORIES, configuredCategory].map(category => String(category).trim()).filter(Boolean),
            ),
        );
        const messageCategoryOptions: ISelectOption<string>[] = messageCategoryValues.map(category => ({
            label: category,
            value: category,
        }));
        const configuredCategoryOrFallback = configuredCategory || 'PAYER_FOLLOWUP';
        const selectedMessageCategory =
            messageCategoryOptions.find(option => option.value === configuredCategoryOrFallback) ??
            messageCategoryOptions[0];
        const isEnterTaskOwner = isEnterTaskMessageOwner(actionValue.messageOwner);
        const actionError = store.getRuleActionError(rule.id);
        const canSubmitCategory = categoryDraft.trim().length > 0;

        const closeCategoryComposer = (): void => {
            setIsCreatingCategory(false);
            setCategoryDraft('');
        };

        const createMessageCategory = (): void => {
            const nextCategory = categoryDraft.trim();
            if (!nextCategory || disabled) {
                return;
            }
            store.setRuleActionValue(rule.id, 'messageCategory', nextCategory);
            closeCategoryComposer();
        };

        return (
            <S.ActionPanel>
                <S.ActionRow>
                    <S.FieldLabel style={{ marginBottom: 0, flex: 'none' }}>Action</S.FieldLabel>
                    <S.FieldSelect
                        options={actionSelectOptions}
                        value={selectedActionOption}
                        required
                        disabled={disabled}
                        onChange={selected => {
                            const option = selected as ISelectOption<string> | null;
                            if (option?.value) {
                                store.changeRuleAction(rule.id, option.value as RuleActionType);
                            }
                        }}
                    />
                </S.ActionRow>

                <ConfigurationActionSummary actionType={rule.action.type} />

                {resolvedActionType === 'CLOSE_OUT' && (
                    <div>
                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.closeoutDescription}>
                            Closeout reason
                        </ConfigurationFieldLabel>
                        <S.FieldInput
                            type="text"
                            maxLength={45}
                            disabled={disabled}
                            readOnly={disabled}
                            value={String(actionValue.closeoutDescription ?? '')}
                            onChange={event =>
                                store.setRuleActionValue(
                                    rule.id,
                                    'closeoutDescription',
                                    event.target.value.slice(0, 45),
                                )
                            }
                        />
                    </div>
                )}

                {resolvedActionType === 'SWITCH_TO_PRIVATE_PAY' && (
                    <div>
                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.memo} optional>
                            Invoice memo
                        </ConfigurationFieldLabel>
                        <S.FieldTextarea
                            rows={3}
                            disabled={disabled}
                            value={String(actionValue.memo ?? '')}
                            onChange={event => store.setRuleActionValue(rule.id, 'memo', event.target.value)}
                        />
                    </div>
                )}

                {resolvedActionType === 'CREATE_APPEAL' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div>
                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.prompt} optional>
                                Customize the cover letter
                            </ConfigurationFieldLabel>
                            <S.FieldTextarea
                                rows={3}
                                disabled={disabled}
                                value={String(actionValue.prompt ?? '')}
                                onChange={event => store.setRuleActionValue(rule.id, 'prompt', event.target.value)}
                            />
                        </div>

                        <ConfigurationReconsiderationDocuments
                            ruleId={rule.id}
                            documents={reconsiderationDocuments}
                            store={store}
                            disabled={disabled}
                        />
                    </div>
                )}

                {resolvedActionType === 'CREATE_MESSAGE' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div>
                            <BaseCheckbox
                                label="Group tasks for this rule"
                                checked={rule.group === true}
                                disabled={disabled}
                                onChange={event => store.setRuleGrouped(rule.id, event.target.checked)}
                            />
                            <S.FieldHelper style={{ marginTop: 4 }}>{RULE_FIELD_HELPERS.group}</S.FieldHelper>
                        </div>
                        <div>
                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.messageOwner}>
                                Responsibility owner
                            </ConfigurationFieldLabel>
                            <SegmentedControl aria-disabled={disabled}>
                                <SegmentButton
                                    type="button"
                                    active={actionValue.messageOwner !== 'PROVIDER'}
                                    disabled={disabled}
                                    onClick={() => {
                                        if (!disabled) {
                                            store.setRuleActionValue(rule.id, 'messageOwner', 'ENTER');
                                        }
                                    }}
                                >
                                    ENTER
                                </SegmentButton>
                                <SegmentButton
                                    type="button"
                                    variant="neutral"
                                    active={actionValue.messageOwner === 'PROVIDER'}
                                    disabled={disabled}
                                    onClick={() => {
                                        if (!disabled) {
                                            store.setRuleActionValue(rule.id, 'messageOwner', 'PROVIDER');
                                        }
                                    }}
                                >
                                    PROVIDER
                                </SegmentButton>
                            </SegmentedControl>
                        </div>

                        <S.TwoColumnFields>
                            <div>
                                <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.messageCategory}>
                                    Category
                                </ConfigurationFieldLabel>
                                <S.CategorySelectRow>
                                    <S.CategorySelectWrap>
                                        <S.FieldSelect
                                            options={messageCategoryOptions}
                                            value={selectedMessageCategory}
                                            required
                                            disabled={disabled}
                                            onChange={selected => {
                                                const option = selected as ISelectOption<string> | null;
                                                if (option?.value) {
                                                    store.setRuleActionValue(rule.id, 'messageCategory', option.value);
                                                }
                                            }}
                                        />
                                    </S.CategorySelectWrap>
                                    {isCreatingCategory ? (
                                        <>
                                            <S.CategoryCreateInput
                                                type="text"
                                                autoFocus
                                                disabled={disabled}
                                                placeholder="New category"
                                                value={categoryDraft}
                                                onChange={event => setCategoryDraft(event.target.value)}
                                                onKeyDown={event => {
                                                    if (event.key === 'Enter') {
                                                        event.preventDefault();
                                                        createMessageCategory();
                                                    }
                                                    if (event.key === 'Escape') {
                                                        event.preventDefault();
                                                        closeCategoryComposer();
                                                    }
                                                }}
                                            />
                                            <S.ConfirmCreateCategoryButton
                                                type="button"
                                                disabled={disabled || !canSubmitCategory}
                                                onClick={createMessageCategory}
                                            >
                                                Create
                                            </S.ConfirmCreateCategoryButton>
                                        </>
                                    ) : (
                                        <S.CreateCategoryButton
                                            type="button"
                                            disabled={disabled}
                                            onClick={() => {
                                                if (!disabled) {
                                                    setIsCreatingCategory(true);
                                                }
                                            }}
                                        >
                                            + Create new category
                                        </S.CreateCategoryButton>
                                    )}
                                </S.CategorySelectRow>
                            </div>
                            <div>
                                <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.assigned}>
                                    Assigned
                                </ConfigurationFieldLabel>
                                <ConfigurationAssignedUsers
                                    disabled={disabled}
                                    loading={store.assignableUsersLoading}
                                    assignedBankUserIds={(actionValue.assigned as string[] | undefined) ?? []}
                                    users={store.assignableUsers}
                                    onChange={bankUserIds => store.setAssignedUsers(rule.id, bankUserIds)}
                                />
                            </div>
                        </S.TwoColumnFields>

                        <div>
                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.suggestedResolution}>
                                Suggested next steps
                            </ConfigurationFieldLabel>
                            <S.FieldTextarea
                                rows={2}
                                disabled={disabled}
                                readOnly={disabled}
                                value={String(actionValue.suggestedResolution ?? '')}
                                onChange={event =>
                                    store.setRuleActionValue(rule.id, 'suggestedResolution', event.target.value)
                                }
                            />
                            {isEnterTaskOwner && (
                                <S.FieldHelper style={{ marginTop: 6, marginBottom: 0 }}>
                                    {ENTER_TASK_FEE_DISCLAIMER}
                                </S.FieldHelper>
                            )}
                        </div>
                    </div>
                )}

                {resolvedActionType === 'EXECUTE_ENTER_FUNCTION' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <ConfigurationFunctionBuilder ruleId={rule.id} store={store} disabled={disabled} />

                        <div>
                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.function}>
                                Function
                                <ConfigurationRequiredMark />
                            </ConfigurationFieldLabel>
                            <ConfigurationFunctionEditor
                                // Also disabled while the AI build is in flight — it
                                // unconditionally overwrites `function` with its result on
                                // arrival, so a manual edit typed during that window would
                                // otherwise be silently discarded (PR review, 2026-09-28).
                                disabled={disabled || store.getFunctionBuilder(rule.id).building}
                                value={String(actionValue.function ?? '')}
                                onChange={value => store.setRuleActionValue(rule.id, 'function', value)}
                            />
                            <ConfigurationFieldError message={actionError} />
                        </div>

                        <div>
                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.disableCodingRules}>
                                Coder rules
                            </ConfigurationFieldLabel>
                            <ConfigurationCoderRulesSelect
                                disabled={disabled}
                                loading={store.coderRulesLoading}
                                error={store.coderRulesError}
                                coderRules={store.coderRules}
                                disableCodingRules={actionValue.disableCodingRules}
                                onChange={ruleNumbers => store.setDisabledCodingRules(rule.id, ruleNumbers)}
                            />
                        </div>
                    </div>
                )}
            </S.ActionPanel>
        );
    },
);
